// Admin scope API — DTOs + fetch wrappers for /api/v1/admin/scope/*.
//
// Mirrors the backend contract in src/rentallens/api/admin_scope.py. No
// mock layer here yet — the operator scope page is admin-only and not
// part of the storybook flow. If we later need offline dev for this
// page, add a mock module like adminScraperMocks.ts.

export type ZipStatus = 'pending' | 'enumerating' | 'draining' | 'ready' | 'disabled';

export interface ZipCodeRecord {
  zip_code: string;
  status: ZipStatus;
  scraper_enabled: boolean;
  bbr_total: number | null;
  bbr_done: number | null;
  enumerated_at: string | null;
  drain_started_at: string | null;
  ready_at: string | null;
  added_at: string;
  added_by: string | null;
  note: string | null;
}

export interface ZipListResponse {
  items: ZipCodeRecord[];
  total: number;
}

export interface AddZipRequest {
  zip_code: string;
  note?: string | null;
}

export interface AddZipResponse {
  record: ZipCodeRecord;
  job_id: string;
  detail: string;
}

export async function fetchScopeZips(includeDisabled = true): Promise<ZipListResponse> {
  const params = new URLSearchParams();
  params.set('include_disabled', String(includeDisabled));
  const res = await fetch(`/api/v1/admin/scope/zips?${params.toString()}`);
  if (!res.ok) throw new Error(`scope_zips_${res.status}`);
  return (await res.json()) as ZipListResponse;
}

export async function addScopeZip(body: AddZipRequest): Promise<AddZipResponse> {
  const res = await fetch('/api/v1/admin/scope/zips', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`add_zip_${res.status}: ${detail}`);
  }
  return (await res.json()) as AddZipResponse;
}

export async function enableScraper(zip: string): Promise<ZipCodeRecord> {
  const res = await fetch(
    `/api/v1/admin/scope/zips/${encodeURIComponent(zip)}/enable-scraper`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(`enable_scraper_${res.status}`);
  return (await res.json()) as ZipCodeRecord;
}

export async function disableScraper(zip: string): Promise<ZipCodeRecord> {
  const res = await fetch(
    `/api/v1/admin/scope/zips/${encodeURIComponent(zip)}/disable-scraper`,
    { method: 'POST' }
  );
  if (!res.ok) throw new Error(`disable_scraper_${res.status}`);
  return (await res.json()) as ZipCodeRecord;
}

export async function deleteScopeZip(zip: string): Promise<ZipCodeRecord> {
  const res = await fetch(
    `/api/v1/admin/scope/zips/${encodeURIComponent(zip)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(`delete_zip_${res.status}`);
  return (await res.json()) as ZipCodeRecord;
}

// Status badge helper — used by the dashboard table to colour-code
// each row. Same Tailwind palette as the rest of the admin UI.
export function statusBadgeClass(status: ZipStatus, scraperEnabled: boolean): string {
  if (status === 'disabled') return 'bg-rule text-fg-muted';
  if (status === 'pending') return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400';
  if (status === 'enumerating' || status === 'draining') {
    return 'bg-blue-500/15 text-blue-700 dark:text-blue-400';
  }
  // ready
  return scraperEnabled
    ? 'bg-green-500/15 text-green-700 dark:text-green-400'
    : 'bg-gray-500/15 text-gray-700 dark:text-gray-400';
}
